<template>
  <div v-if="visible" class="mt-6">
    <div v-if="!input[content.type]">
      <p class="py-2 text-gray-400">Данное поле пока что не поддерживается</p>
    </div>
    <div v-else class="flex flex-col">
      <slot name="label" :content="content" :asterisk-classes="asteriskClasses">
        <div class="mb-4 max-w-xl">
          <UIHeading
            :title="content.title"
            tag="4"
            :class="
              [...(content.required ? asteriskClasses : [])].filter(Boolean)
            "
          />
        </div>
      </slot>

      <component
        :is="input[content.type]"
        :context="content"
        :accept="content?.accept"
        :global-model="globalModel"
        :uuid="uuid"
        v-model="model"
        :passed="passed"
        :current="current"
        :loading="disableIfLoading"
      />

      <ul v-if="errors.length" class="mt-4 text-rose-400">
        <li v-for="e in errors" v-text="e" />
      </ul>
    </div>

    <slot name="errors" />
  </div>
</template>

<script setup>
import { intersection, isEqual } from 'lodash-es'
import { computed, markRaw, ref } from 'vue'

import Address from '@/components/player/components/Address.vue'
import AddressChoice from '@/components/player/components/AddressChoice.vue'
import Bool from '@/components/player/components/Bool.vue'
import Choice from '@/components/player/components/Choice.vue'
import Date from '@/components/player/components/Date.vue'
import File from '@/components/player/components/File.vue'
import Fio from '@/components/player/components/Fio.vue'
import Mark from '@/components/player/components/Mark.vue'
import Mask from '@/components/player/components/Mask.vue'
import Money from '@/components/player/components/Money.vue'
import Municipality from '@/components/player/components/Municipality.vue'
import Phone from '@/components/player/components/Phone.vue'
import Portfolio from '@/components/player/components/Portfolio.vue'
import Text from '@/components/player/components/Text.vue'
import UIHeading from '@/components/ui/UIHeading.vue'

import Table from './Table.vue'
import Template from './Template.vue'

const props = defineProps({
  scheme: {
    type: Object,
    default: () => {},
  },
  uuid: {
    type: String,
  },
  content: {
    type: Object,
  },
  conditions: {
    type: Array,
  },
  passed: {
    type: Boolean,
    default: false,
  },
  current: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    default: null,
  },
  globalModel: {
    type: Object,
    default: () => {},
  },
  errors: {
    type: Array,
    default: () => [],
  },
})

const emits = defineEmits(['update:model-value', 'loading'])

const model = computed({
  get: () => {
    return props.modelValue
  },
  set: (value) => {
    emits('update:model-value', value)
  },
})
const input = ref({
  fio: markRaw(Fio),
  text: markRaw(Text),
  textarea: markRaw(Text),
  number: markRaw(Text),
  email: markRaw(Text),
  link: markRaw(Text),
  bool: markRaw(Bool),
  mark: markRaw(Mark),
  date: markRaw(Date),
  file: markRaw(File),
  phone: markRaw(Phone),
  money: markRaw(Money),
  choice: markRaw(Choice),
  address: markRaw(Address),
  municipality: markRaw(Municipality),
  address_choice: markRaw(AddressChoice),
  relation: null,
  distribution: null,
  child: markRaw(Portfolio),
  mask: markRaw(Mask),
  template: markRaw(Template),
  table: markRaw(Table),
})

function disableIfLoading(value) {
  emits('loading', value)
}

const visible = computed(() => {
  if (!props.conditions) {
    return true
  }

  return props.conditions.every((i) => {
    switch (i.type) {
      case 'equals':
        return isEqual(props.globalModel[i.field], i.value)
      case 'contains':
        return intersection(
          Object.values(props.globalModel[i.field] ?? {}),
          Object.values(i.value ?? {}),
        ).length
      case 'not_contains':
        return (
          intersection(
            Object.values(props.globalModel[i.field] ?? {}),
            Object.values(i.value ?? {}),
          ).length === 0
        )
      case 'not_equals':
        return !isEqual(props.globalModel[i.field], i.value)
      case 'empty':
        return !props.globalModel[i.field]
      case 'not_empty':
        return !!props.globalModel[i.field]
      default:
        return true
    }
  })
})

const asteriskClasses = ref([
  'after:text-2xl',
  'after:ml-1',
  'after:text-rose-800',
  "after:content-['*']",
])
</script>
